import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";

export const Route = createFileRoute("/toimitus")({
  head: () => ({
    meta: [
      { title: "Toimitus Suomeen — ilmainen ja seurattu 3–7 pv | Montessori24" },
      { name: "description", content: "Ilmainen ja seurattu toimitus Suomeen 3–7 arkipäivässä. Toimitustavat, -ajat ja -kulut Postin ja Matkahuollon kautta." },
      { property: "og:title", content: "Toimitus Suomeen — ilmainen ja seurattu 3–7 pv" },
      { property: "og:description", content: "Ilmainen seurattu toimitus Suomeen 3–7 arkipäivässä." },
      { property: "og:url", content: "https://montessori24.fi/toimitus" },
    ],
    links: [{ rel: "canonical", href: "https://montessori24.fi/toimitus" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Milloin tilaukseni lähtee?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Varastossa olevat tuotteet pyritään lähettämään 1–2 arkipäivän kuluessa. Saat seurantakoodin heti, kun lähetys on luovutettu kuljetusliikkeelle.",
            },
          },
          {
            "@type": "Question",
            name: "Saanko seurantakoodin?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Kyllä. Saat sähköpostilla seurantakoodin heti, kun lähetys on luovutettu kuljetusliikkeelle.",
            },
          },
          {
            "@type": "Question",
            name: "Toimitatteko Ahvenanmaalle?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Kyllä, toimitamme koko Suomen alueelle. Ahvenanmaan toimitusaika on tyypillisesti 1–2 arkipäivää pidempi.",
            },
          },
          {
            "@type": "Question",
            name: "Toimitatteko muualle Eurooppaan?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Toimitamme Ruotsiin, Viroon, Tanskaan ja Saksaan. Toimituskulut näytetään kassalla.",
            },
          },
          {
            "@type": "Question",
            name: "Tilaukseni ei ole saapunut, mitä teen?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Jos pakettisi ei ole saapunut arvioidussa ajassa, ota yhteyttä osoitteeseen asiakaspalvelu@montessori24.fi seurantakoodin kanssa — selvitämme tilanteen kuljetusliikkeen kanssa.",
            },
          },
        ],
      }),
    }],
  }),
  component: Shipping,
});

const methods = [
  ["Posti Pakettiautomaatti", "3–7 arkipäivää", "0 €"],
  ["Posti Kotijakelu", "3–7 arkipäivää", "0 €"],
  ["Matkahuolto Lähellä", "3–7 arkipäivää", "0 €"],
  ["Nouto Helsinki (Punavuori)", "Sovittaessa", "0 €"],
] as const;

const faqs = [
  ["Milloin tilaukseni lähtee?", "Varastossa olevat tuotteet pyritään lähettämään 1–2 arkipäivän kuluessa. Saat seurantakoodin heti, kun lähetys on luovutettu kuljetusliikkeelle."],
  ["Saanko seurantakoodin?", "Kyllä. Saat sähköpostilla seurantakoodin heti, kun lähetys on luovutettu kuljetusliikkeelle."],
  ["Toimitatteko Ahvenanmaalle?", "Kyllä, toimitamme koko Suomen alueelle. Ahvenanmaan toimitusaika on tyypillisesti 1–2 arkipäivää pidempi."],
  ["Toimitatteko muualle Eurooppaan?", "Toimitamme Ruotsiin, Viroon, Tanskaan ja Saksaan. Toimituskulut näytetään kassalla."],
  ["Tilaukseni ei ole saapunut, mitä teen?", "Jos pakettisi ei ole saapunut arvioidussa ajassa, ota yhteyttä osoitteeseen asiakaspalvelu@montessori24.fi seurantakoodin kanssa — selvitämme tilanteen kuljetusliikkeen kanssa."],
] as const;

function Shipping() {
  return (
    <Layout>
      <section className="container-prose pt-16 pb-12 md:pt-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Asiakaspalvelu</span>
            <h1 className="mt-3 text-5xl text-foreground md:text-6xl text-balance">Toimitus</h1>
            <p className="mt-6 text-muted-foreground">
              Nopea ja seurattu toimitus koko Suomeen. Toimitus on aina ilmainen — kaikkiin tilauksiin.
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="rounded-2xl border border-border bg-cream p-8">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Toimitustavat</div>
              <table className="mt-6 w-full text-sm">
                <tbody>
                  {methods.map(([name, time, price]) => (
                    <tr key={name} className="border-t border-border first:border-0">
                      <td className="py-4 pr-4 text-foreground">{name}</td>
                      <td className="py-4 pr-4 text-muted-foreground">{time}</td>
                      <td className="py-4 text-right text-foreground">{price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-6 text-xs text-muted-foreground">
                Tarjoamme ilmaisen toimituksen kaikkiin Suomen tilauksiin — ilman tilausrajaa.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-prose pb-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <h2 className="text-3xl text-foreground md:text-4xl">Usein kysyttyä toimituksesta</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Lue myös <Link to="/palautus" className="text-foreground underline-offset-4 hover:underline">palautusohjeet</Link> tai
              kaikki vastaukset <Link to="/ukk" className="text-foreground underline-offset-4 hover:underline">UKK-sivulla</Link>.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <dl className="divide-y divide-border border-y border-border">
              {faqs.map(([q, a]) => (
                <details key={q} className="group py-6">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-6 text-base text-foreground">
                    <span>{q}</span>
                    <span className="mt-1 text-xl text-muted-foreground transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 max-w-prose text-sm leading-relaxed text-muted-foreground">{a}</p>
                </details>
              ))}
            </dl>
          </div>
        </div>
      </section>
    </Layout>
  );
}
